'use client';

import { useCallback, useMemo, useState } from 'react';
import { Menu as MenuPrimitive } from '@base-ui/react';
import { cn } from '../../../lib/utils';
import {
  MenuSizeProvider,
  type MenuSizeContextValue,
  type MenuSize,
} from '../shared/menu-size-context';
import {
  PopoverMenuRootProvider,
  usePopoverMenuRootContext,
  type PopoverMenuRootContextValue,
} from './popover-menu-root-context';

export type PopoverMenuProps = MenuPrimitive.Root.Props;

export const PopoverMenu = ({
  children,
  onOpenChange,
  onOpenChangeComplete,
  ...props
}: PopoverMenuProps) => {
  const [instantClose, setInstantClose] = useState(false);

  const handleOpenChange = (open: boolean, eventDetails: MenuPrimitive.Root.ChangeEventDetails) => {
    if (open) {
      setInstantClose(false);
    }

    onOpenChange?.(open, eventDetails);
  };

  const handleOpenChangeComplete = (open: boolean) => {
    if (!open) {
      setInstantClose(false);
    }

    onOpenChangeComplete?.(open);
  };

  const skipExitAnimation = useCallback(() => setInstantClose(true), []);
  const rootContextValue = useMemo<PopoverMenuRootContextValue>(
    () => ({ instantClose, skipExitAnimation }),
    [instantClose, skipExitAnimation],
  );

  return (
    <PopoverMenuRootProvider value={rootContextValue}>
      <MenuPrimitive.Root
        onOpenChange={handleOpenChange}
        onOpenChangeComplete={handleOpenChangeComplete}
        {...props}>
        {children}
      </MenuPrimitive.Root>
    </PopoverMenuRootProvider>
  );
};

export const PopoverMenuTrigger = ({ ...props }: MenuPrimitive.Trigger.Props) => (
  <MenuPrimitive.Trigger data-slot="popover-menu-trigger" {...props} />
);

export type PopoverMenuSurfaceProps = Omit<MenuPrimitive.Popup.Props, 'className'> & {
  className?: string;
  side?: MenuPrimitive.Positioner.Props['side'];
  align?: MenuPrimitive.Positioner.Props['align'];
  sideOffset?: MenuPrimitive.Positioner.Props['sideOffset'];
  alignOffset?: MenuPrimitive.Positioner.Props['alignOffset'];
};

export const PopoverMenuSurface = ({
  className,
  side,
  align,
  sideOffset,
  alignOffset,
  children,
  ...props
}: PopoverMenuSurfaceProps) => {
  const { instantClose } = usePopoverMenuRootContext();

  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        // z-[10001] — must exceed aura modals overlay/container z-[10000] so menus render above the modal
        // pointer-events-auto — Radix Dialog (modal=true) sets pointer-events:none on body siblings incl. portals; restore it here
        className="pointer-events-auto z-[10001]">
        <MenuPrimitive.Popup
          data-instant-close={instantClose ? '' : undefined}
          className={cn(
            // layout + sizing
            'flex min-w-[198px] flex-col gap-0.5',
            // cap long lists at the space the positioner reports
            'max-h-(--available-height) overflow-y-auto overscroll-contain scrollbar-thin *:shrink-0',
            // shape + surface (Figma menu container)
            'rounded-2xl border border-border-default bg-surface-primary p-2 shadow-bb-shadow-md outline-none',
            // animation
            'origin-(--transform-origin) duration-100',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
            'data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            'data-[instant-close]:animate-none',
            className,
          )}
          {...props}>
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
};

export type PopoverMenuContentProps = PopoverMenuSurfaceProps & {
  /**
   * Row density applied to every item, heading and divider inside.
   * @default 'sm'
   */
  size?: MenuSize;
};

export const PopoverMenuContent = ({
  size = 'sm',
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  children,
  ...props
}: PopoverMenuContentProps) => {
  const contextValue = useMemo<MenuSizeContextValue>(() => ({ size }), [size]);

  return (
    <PopoverMenuSurface
      data-slot="popover-menu-content"
      side={side}
      align={align}
      sideOffset={sideOffset}
      {...props}>
      <MenuSizeProvider value={contextValue}>{children}</MenuSizeProvider>
    </PopoverMenuSurface>
  );
};
